import { inject, Injectable, signal } from '@angular/core';
import { ScriptLoaderService } from '../script-loader.service';
import { catchError, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import { UserService } from '../user/user.service';
import { LanguageService } from '../language/language.service';
import { PlatformService } from '../platform.service';
import { CookieService } from 'ngx-unificator/services';
import { StaticContentService } from '../static-content.service';
import { ZENDESK_SCRIPT_URL } from './zendesk-chat-data';
import { environment } from '../../../../environments/environment';
import { ServiceWorkerService } from '../service-worker.service';
import { unreadMessagesCount } from '../../helpers/signals';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BrandConfigService } from '../brand-config/brand-config.service';

declare var zE;

const BRAND_ID = 7;

@Injectable({
  providedIn: 'root'
})

export class ZendeskChatService {
  private _scriptLoader = inject(ScriptLoaderService);
  private _user = inject(UserService);
  private _lang = inject(LanguageService);
  private _platform = inject(PlatformService);
  private _cookie = inject(CookieService);
  private _static = inject(StaticContentService);
  private _pwa = inject(ServiceWorkerService);
  private _http = inject(HttpClient);
  private _brandConfig = inject(BrandConfigService);


  /**
   * Returns true if zendesk chat available
   */
  public available = signal(false);

  private _currentTimestamp = 0;

  /**
   * Returns true if chat open
   * @private
   */
  private _isOpen: boolean;

  private _ignoreZendeskAuth = false;

  get isOpen() {
    return this._isOpen;
  }

  /**
   * Get widget api key
   * @private
   */
  private _getWidgetApiKey$() {
    return this._static.item({slug: 'zendesk-api-key'}).pipe(
      filter(data => !!data && data[0]),
      map(data => data[0].Content && data[0].Content.trim())
    );
  }

  /**
   * Init zendesk chat
   */
  public initChat() {
    if (this._platform.isBrowser) {
      this.available.set(false);
      this._brandConfig.brandConfigResolved$.pipe(
        switchMap(() => this._getWidgetApiKey$()),
        filter(key => !!key),
        first(),
        switchMap(key => this._scriptLoader.load(`${ZENDESK_SCRIPT_URL}?key=${key}&timestamp=${this._currentTimestamp}`,
          undefined, { id: 'ze-snippet', defer: '' })),
        tap(async() => {
          this.available.set(true);
          await this.setInitFunctionalData();
          zE('messenger', 'close');
        }),
        catchError(error => {
          this.available.set(false);
          return of(error);
        }),
      ).subscribe();
    }
  }


  /**
   * Set init data for functional chat
   * @public
   */
  public async setInitFunctionalData() {
    this.resetWidget();
    this.hide();

    if (this._user.auth && !this._ignoreZendeskAuth) {
      await this.setUserData();
    }
    this._onClose();
    this._onChangeLanguage();
    this._onMessage();
    this._addTags(this.buildTags());
  }

  /**
   * Toggle chat
   * @open
   */
  public toggle() {
    this.isOpen ? this.hide() : this.open();
  }

  /**
   * Hide chat
   */
  public hide() {
    zE('messenger', 'close');
  }

  /**
   * Open chat
   */
  public open() {
    zE('messenger', 'open');
  }

  public resetWidget(callback?: () => void) {
    if (zE) {
      zE("messenger", "resetWidget", callback);
    }
  }


  /**
   * Handler on close widget
   * @private
   */
  private _onClose() {
    if (this._platform.isBrowser) {
      zE('messenger:on', 'close', () => {
        this._isOpen = false;
        this._removeUnreadDotFromFavicon();
      });
    }
  }

  /**
   * Set locale for chat
   * @private
   * @locale
   */
  private _setLocale(locale) {
    if (this.available()) {
      zE('messenger:set', 'locale', locale);
    }
  }

  /**
   * Add tags
   * @private
   * @tags
   */
  private _addTags(tags: string[]) {
    zE('messenger:set', 'conversationTags', tags);
  }

  /**
   * Change locale for chat
   * @private
   */
  private _onChangeLanguage() {
    this._lang.langChange$.pipe(
      tap(locale => this._setLocale(locale))
    ).subscribe();
  }

  /**
   * Open chat if message coming
   * @private
   */
  private _onMessage() {
    if (this._platform.isBrowser && this.available() && zE) {
      zE('messenger:on', 'unreadMessages', countMessage => {
        unreadMessagesCount.set(countMessage);
        if (!this._isOpen && countMessage > 0) {
          this._pwa.stopGifAnimation();
          this.open();
          this._addUnreadDotToFavicon();
        } else {
          this._removeUnreadDotFromFavicon();
        }
      });
    }
  }

  private _updateFavicon(favicon: HTMLLinkElement, newFaviconUrl: string) {
    favicon.href = newFaviconUrl;
  }
  private _addUnreadDotToFavicon() {
    if (this._platform.isBrowser) {
      const favicons = this._getFavicons();

      favicons.forEach((favicon) => {
        const originalFavicon = favicon.href?.includes('assets') ? favicon.href : null;
        if (!originalFavicon) return;

        favicon.dataset.originalHref = originalFavicon;

        const canvas = document.createElement('canvas');
        const img = new Image();
        img.src = originalFavicon;
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;

          const context = canvas.getContext('2d');
          if (context) {
            context.drawImage(img, 0, 0);

            // Draw the red dot
            const dotSize = Math.max(6, img.width / 6); // Adjust dot size dynamically
            const xPos = canvas.width - dotSize;
            const yPos = dotSize;

            context.beginPath();
            context.arc(xPos, yPos, dotSize, 0, 2 * Math.PI);
            context.fillStyle = 'red';
            context.fill();

            this._updateFavicon(favicon, canvas.toDataURL('image/png'));
          }
        };
      });
    }
  }

  private _removeUnreadDotFromFavicon() {
    const favicons = this._getFavicons();
    favicons.forEach((favicon) => {
      const originalHref = favicon.dataset.originalHref;
      if (originalHref) {
        favicon.href = originalHref;
      }
    });
  }

  private _getFavicons(): NodeListOf<HTMLLinkElement> {
    return document.querySelectorAll('link[rel*="icon"]');
  }

  /**
   * Set user data (name, email)
   * @param data
   * @private
   */
  public async setUserData() {
    const userObj = {
      user_id: `${this._user.info.id}`,
      first_name: this._user.info.first_name,
      last_name: this._user.info.last_name,
      date_of_birth: this._user.info.date_of_birth,
      email: this._user.info.email
    };

    if (Object.values(userObj).every((e) => !!e)) {
      this.available.set(false);
      const token = await this.fetchAuthToken(userObj);

      if (token) {
        zE("messenger", "loginUser", (callback) => {
          callback(token);
        }, (error) => {
          this.available.set(true);
          if (error) {
            this._ignoreZendeskAuth = true;
            this.resetWidget(() => {
              this.setInitFunctionalData();
            });
          }
        });
      } else {
        this.available.set(true);
      }
    }
  }

  private async fetchAuthToken(userObj: any): Promise<string | null> {
    const headers = new HttpHeaders({
      'Content-Type': 'text/plain;charset=UTF-8',
      'Accept': '*/*',
    });

    const response = await firstValueFrom(
      this._http
        .post<{ token: string }>(`${environment.zendesk_host}/auth`, userObj, {
          headers,
        })
        .pipe(catchError(err => of(err))),
    );

    if (!response || !response.token) {
      return null;
    }

    return response.token;
  }

  private buildTags(): string[] {
    return [
      this._lang.current ?? '',
      `brandid-${BRAND_ID}`,
      this._user.info?.id ? `userid-${this._user.info.id}` : '',
      this._user.auth ? 'authorized' : 'non-authorized',
      this._cookie.get('id_id') ?? ''
    ];
  }
}

